import { IntlKey } from 'locales';
import { FC, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import { getOrdersBasketNumberOfFormsByProductSubType } from 'state/orders-basket/selectors';
import { ordersBasketProductSelectedThunk } from 'state/orders-basket/thunks';
import { getProductVariations } from 'state/product-description/selectors';
import type { ThunkDispatch } from 'state/state.common';
import { AppState } from 'state/state.model';
import { OrdersBasketProductSelectorOption } from 'state/workspaces/ordersBasket/ordersBasket.model';
import { getWorkspaceTabs } from 'state/workspaces/tabs/tabs.selector';
import { getWorkspaceActiveTabId } from 'state/workspaces/workspaces/workspaces.selector';

interface OrdersBasketProductSelectorProps {}

const selectProductVariations = createSelector([(state: AppState) => state.productDescription], productDescription =>
  getProductVariations(productDescription, 'OrdersBasket'),
);

export const OrdersBasketProductSelector: FC<OrdersBasketProductSelectorProps> = () => {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch<ThunkDispatch>();
  const productVariations = useSelector((state: AppState) => selectProductVariations(state));
  const tabs = useSelector((state: AppState) => getWorkspaceTabs(state, 'ordersBasket'));
  const activeTabId = useSelector((state: AppState) => getWorkspaceActiveTabId(state, 'ordersBasket'));
  const numberOfFormsBySubType = useSelector((state: AppState) => getOrdersBasketNumberOfFormsByProductSubType(state));

  const productOptions: OrdersBasketProductSelectorOption[] = useMemo(() => {
    return productVariations.map(({ productSubType, productType }) => {
      const label = formatMessage({
        id: `quote.products.${productSubType}` as IntlKey,
        defaultMessage: productSubType,
      });
      const matchedTab = tabs.find(tab => tab.name === label);
      return {
        id: matchedTab?.id,
        productSubTypeLabel: label,
        productSubType,
        productType,
        numberOfForms: numberOfFormsBySubType[productSubType] || 0,
      };
    });
  }, [productVariations, tabs, numberOfFormsBySubType, formatMessage]);

  const handleChange = useCallback(
    (selectedProductOption: OrdersBasketProductSelectorOption) => {
      dispatch(ordersBasketProductSelectedThunk(selectedProductOption));
    },
    [dispatch],
  );

  return (
    <div className="d-flex">
      {productOptions.map(option => {
        const inputId = `orders-basket-product-selector-${option.productSubType}`;
        return (
          <div className="form-check" key={option.id || option.productSubType}>
            <input
              className="form-check-input"
              type="radio"
              value={option.productSubType}
              id={inputId}
              aria-label={option.productSubTypeLabel}
              checked={!!activeTabId && activeTabId === option.id}
              onChange={() => handleChange(option)}
              data-e2e={inputId}
            />
            <label className="form-check-label" htmlFor={inputId} id={inputId + '-label'}>
              {option.productSubTypeLabel}
              {option.numberOfForms > 0 && (
                <span className="badge badge-discreet-info rounded-pill ms-2">{option.numberOfForms}</span>
              )}
            </label>
          </div>
        );
      })}
    </div>
  );
};
